import { supabase } from "@/integrations/supabase/client";
import { openExternal } from "@/lib/openExternal";
import type { ExitReason } from "@/components/ExitFeedbackQuestion";

export interface DeletionFeedback {
  attempt_id: string;
  reason: ExitReason;
  comment?: string;
}

const API_URL =
  (import.meta.env.VITE_API_URL as string) || "https://api.ondiet.app";

interface BillingPortalResponse {
  success: boolean;
  url: string;
}

export type DeletionConfirmation =
  | { kind: "accepted"; requestRef: string; state: DeletionRequestState }
  | { kind: "legacy_deleted" };

export type DeletionRequestState =
  | "queued"
  | "running"
  | "retry_wait"
  | "needs_attention"
  | "completed";

const deletionRequestStates = new Set<DeletionRequestState>([
  "queued",
  "running",
  "retry_wait",
  "needs_attention",
  "completed",
]);
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const messageForStatus = (status: number): string => {
  if (status === 401) return "Sua sessão expirou — entre novamente.";
  if (status === 409) return "Sua assinatura precisa de revisão. Fale com a equipe.";
  if (status === 429) return "Muitas tentativas seguidas. Aguarde um minuto.";
  return "Não foi possível abrir o Stripe agora. Aguarde um minuto e tente novamente.";
};

/** Cria uma sessão Stripe vinculada à conta já autenticada no app. */
export async function openAuthenticatedBillingPortal(): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Sua sessão expirou — entre novamente.");
  }

  const response = await fetch(`${API_URL}/api/profile/billing-portal-session`, {
    method: "POST",
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!response.ok) throw new Error(messageForStatus(response.status));

  const data = (await response.json()) as Partial<BillingPortalResponse>;
  if (data.success !== true || typeof data.url !== "string") {
    throw new Error("Não foi possível abrir o Stripe agora. Aguarde um minuto e tente novamente.");
  }

  const url = new URL(data.url);
  if (url.protocol !== "https:" || url.hostname !== "billing.stripe.com") {
    throw new Error("O Stripe devolveu um endereço inválido.");
  }
  openExternal(url.toString());
}

async function authenticatedPost(path: string, body?: unknown): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Sua sessão expirou — entre novamente.");
  }
  return fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export async function confirmAccountDeletion(
  feedback?: DeletionFeedback,
): Promise<DeletionConfirmation> {
  const response = await authenticatedPost("/api/profile/account-deletion/confirm", {
    confirmation: "EXCLUIR",
    accept_no_refund: true,
    ...(feedback ? { feedback } : {}),
  });
  if (!response.ok) {
    if (response.status === 409) {
      throw new Error("A exclusão precisa de atendimento da equipe. Sua conta permanece ativa.");
    }
    if (response.status === 401) throw new Error("Sua sessão expirou — entre novamente.");
    if (response.status === 429) throw new Error("Muitas tentativas seguidas. Aguarde um minuto.");
    throw new Error("Não foi possível concluir a exclusão. Tente novamente.");
  }
  const data = (await response.json()) as {
    success?: boolean;
    deleted?: boolean;
    accepted?: boolean;
    request_ref?: string;
    state?: string;
  };
  if (response.status === 200 && data.success === true && data.deleted === true) {
    return { kind: "legacy_deleted" };
  }
  if (
    response.status === 202 &&
    data.success === true &&
    data.accepted === true &&
    typeof data.request_ref === "string" &&
    uuidPattern.test(data.request_ref) &&
    typeof data.state === "string" &&
    deletionRequestStates.has(data.state as DeletionRequestState)
  ) {
    return {
      kind: "accepted",
      requestRef: data.request_ref,
      state: data.state as DeletionRequestState,
    };
  }
  throw new Error("O servidor não confirmou o recebimento da solicitação. Tente novamente.");
}
